export const COMMAND_STAGE_LABELS = {
  server: 'Phone → Server',
  relay: 'Server → Relay',
  targetConnection: 'Relay → Target',
  targetResponse: 'Target response',
  verification: 'Visible state',
};

export const stageSymbol = (state) => {
  if (state === 'succeeded') return '✓';
  if (state === 'failed' || state === 'timed_out') return '✕';
  if (state === 'pending') return '…';
  return '?';
};

export const commandAttemptTone = (attempt) => {
  if (!attempt) return 'idle';
  if (attempt.status === 'pending') return 'pending';
  if (attempt.status === 'failed' || attempt.status === 'timed_out') return 'error';
  if (attempt.status === 'acknowledged' || attempt.status === 'verified') return 'success';
  return 'neutral';
};

const FAILURE_STAGE_MESSAGES = {
  relay_selection: 'No eligible relay could be selected.',
  relay_delivery: 'The command did not reach the relay agent.',
  relay_acceptance: 'The relay did not acknowledge the command.',
  relay_response: 'The relay stopped responding after accepting the command.',
  relay_response_deadline: 'The relay response arrived after the command deadline.',
  target_resolution: 'The target address could not be resolved.',
  target_connect: 'The relay could not open a connection to the target.',
  target_tls: 'The target rejected or could not complete the secure connection.',
  target_readiness: 'The target connected but did not become ready for remote input.',
  target_pairing: 'The target must be paired again before remote input can be used.',
  app_launch_observation: 'The requested app did not remain in the foreground.',
  screensaver_exit: 'The device remained in its dream or screensaver state.',
  dream_wake_dispatch: 'No eligible awake relay could send the non-toggle Home command.',
  player_reconnection: 'The app reached foreground but its player channel did not remain connected.',
  pre_injection_expiry: 'The command expired before any key was injected.',
  pre_injection_cancellation: 'The command was cancelled before any key was injected.',
  uncertain_injection: 'The connection failed after a key write; do not retry automatically.',
};

export const commandFailureStageSummary = (attempt) => {
  const stage = attempt?.failure?.stage;
  if (!stage || !FAILURE_STAGE_MESSAGES[stage]) return null;
  const relay = attempt?.diagnostics?.relayName
    || (attempt?.route?.bridgeDeviceId ? `Relay ${attempt.route.bridgeDeviceId}` : null);
  return relay ? `${relay}: ${FAILURE_STAGE_MESSAGES[stage]}` : FAILURE_STAGE_MESSAGES[stage];
};

export const commandAttemptSummary = (attempt) => {
  if (!attempt) return 'No commands sent yet.';
  if (attempt.status === 'pending') return 'Sending…';
  if (attempt.status === 'failed' || attempt.status === 'timed_out') {
    if (attempt.result?.code === 'bridge_native_channel_missing') {
      const relay = attempt.diagnostics?.relayName
        || (attempt.route?.bridgeDeviceId ? `Relay ${attempt.route.bridgeDeviceId}` : 'Selected relay');
      return `${relay} is offline on the native-agent channel; the target was not contacted.`;
    }
    if (attempt.result?.code === 'bridge_command_delivery_timeout') {
      return 'The relay did not report a command result; delivery to the target is unknown.';
    }
    if (attempt.result?.code === 'command_expired') {
      return 'The command expired and will not be retried.';
    }
    const stageSummary = commandFailureStageSummary(attempt);
    if (stageSummary) return stageSummary;
    return attempt.result?.message || 'Command failed.';
  }
  if (attempt.status === 'acknowledged') return 'Target acknowledged the command.';
  if (attempt.status === 'verified') return 'Visible state verified.';
  if (attempt.status === 'queued') return 'Queued for delivery.';
  return 'Command dispatched; target acknowledgment unavailable.';
};

export const canRetryCommandAttempt = (attempt) => {
  if (!attempt || !['failed', 'timed_out'].includes(attempt.status)) return false;
  if (attempt.failure?.retryDisposition) {
    return attempt.failure.retryDisposition === 'safe_before_delivery';
  }
  return ['relay', 'targetConnection'].some((key) =>
    ['failed', 'timed_out'].includes(attempt.stages?.[key]?.state)
  );
};

export const createPendingCommandAttempt = ({ id, action, transport, target }) => ({
  id,
  action,
  transport,
  status: 'pending',
  target: target || null,
  route: { bridgeDeviceId: null },
  startedAt: new Date().toISOString(),
  completedAt: null,
  durationMs: null,
  stages: {
    server: { state: 'pending', detail: 'Sending request', latencyMs: null },
    relay: { state: 'unknown', detail: null, latencyMs: null },
    targetConnection: { state: 'unknown', detail: null, latencyMs: null },
    targetResponse: { state: 'unknown', detail: null, latencyMs: null },
    verification: { state: 'unsupported', detail: null, latencyMs: null },
  },
  result: { code: null, message: null },
  failure: null,
});

export const commandChannelSummary = (attempt, channel, device = 'bridge') => {
  const stateKey = device === 'target' ? 'targetChannelState' : 'bridgeChannelState';
  const state = attempt?.diagnostics?.[stateKey]?.[channel];
  if (!state) return 'unavailable';
  const status = state.connected ? 'connected' : 'disconnected';
  const observedAt = state.observedAt || (state.connected ? state.lastConnectedAt : state.lastDisconnectedAt);
  return observedAt ? `${status} · observed ${observedAt}` : status;
};

export const extractCommandAttempt = (value) =>
  value?.attempt || value?.payload?.attempt || value?.response?.data?.attempt || null;

export const commandConnectionSummary = (attempt) => {
  const diagnostics = attempt?.diagnostics;
  if (!diagnostics || attempt?.transport !== 'android_tv_remote') return null;
  if (diagnostics.connectionState === 'failed') return 'Target connection failed';
  if (diagnostics.connectionReused) {
    const idle = diagnostics.connectionIdleMs != null ? ` · idle age ${diagnostics.connectionIdleMs} ms` : '';
    return `Warm target connection${idle}`;
  }
  const ready = diagnostics.connectionReadyMs != null ? ` · ready in ${diagnostics.connectionReadyMs} ms` : '';
  return `New target connection${ready}`;
};

const TRANSPORT_LABELS = {
  accessibility: 'Suitestream Accessibility',
  android_tv_remote: 'Google TV Remote',
  adb_remote: 'ADB Remote',
  assistant: 'Google Assistant',
  cast: 'Google Cast',
  suitestream_app: 'Suitestream App',
  webos: 'LG WebOS',
  wol: 'Wake-on-LAN',
};

export const commandTransportLabel = (attempt) => {
  const transport = attempt?.diagnostics?.executionMode || attempt?.transport;
  if (!transport) return 'Unknown';
  return TRANSPORT_LABELS[transport]
    || String(transport).replaceAll('_', ' ');
};

export const commandExecutionLabel = (attempt) => {
  const diagnostics = attempt?.diagnostics || {};
  const targetName = diagnostics.executionTargetName || attempt?.target?.name || 'Unknown target';
  if (!['android_tv_remote', 'adb_remote'].includes(diagnostics.executionMode || attempt?.transport)) {
    return targetName;
  }
  const relayName = diagnostics.relayName
    || (attempt?.route?.bridgeDeviceId ? `Relay ${attempt.route.bridgeDeviceId}` : 'Unknown relay');
  return `${relayName} → ${targetName}`;
};

export const commandHistorySpacing = (history, index) => {
  const currentStartedAt = Date.parse(history?.[index]?.startedAt || '');
  const previousStartedAt = Date.parse(history?.[index + 1]?.startedAt || '');
  if (!Number.isFinite(currentStartedAt) || !Number.isFinite(previousStartedAt)) return null;
  return Math.max(0, currentStartedAt - previousStartedAt);
};
